import React, { useContext, useEffect, useRef, useState } from "react";
import intl from "react-intl-universal";
import styled from "styled-components";
import { mdiTrashCanOutline } from "@mdi/js";

import { Grid } from "../../../../../components/Grid";
import { Button } from "../../../../../components/Button";
import { Output } from "../../../../../components/Output";
import { Spacer } from "../../../../../components/Spacer";
import { InputText } from "../../../../../components/InputText";
import { SelectSingle } from "../../../../../components/SelectSingle";
import ixusUtils from "../../../../../../script/utils/ixusUtils";
import safetyDistanceUtils from "../../../../../../script/utils/safetyDistanceUtils";

import { EntryContext } from "../../../../../contexts/EntryContext";

const LayerStyle = styled.div`
    display: ${props => (props.$show ? "flex" : "none")};
`;

export const MeasurementLayer = props => {
    const { show, plot, selectedIndex } = props;

    const entryContext = useContext(EntryContext);
    const { entry } = entryContext;
    const { plans } = entry;
    const ixusSetting = entry.ixusSetting || {};
    const ixusAreas = ixusSetting.ixusAreas || [];

    const viewLayerName = "ixusMeasureViewLayer";
    const measureLayerName = "ixusMeasureLayer";

    const [selectedMeasurement, setSelectedMeasurement] = useState();
    const [armedDirection, setArmedDirection] = useState();
    const [levelPlanKey, setLevelPlanKey] = useState();
    const [note, setNote] = useState(null);

    const { directions } = safetyDistanceUtils;

    const selectedIxusArea = selectedIndex !== undefined ? ixusAreas[selectedIndex] : undefined;
    const selectedPlan = plans.find(plan => plan.key === selectedIxusArea?.planKey);
    const levels = safetyDistanceUtils.getLevels(ixusAreas, plans);

    // On a top view the measured distance belongs to that view, on a side view to the
    // level picked in the dropdown.
    const targetPlanKey = selectedIxusArea?.sideView ? levelPlanKey : selectedIxusArea?.planKey;
    const availableDirections = selectedIxusArea?.sideView
        ? [directions.TOP_BOTTOM]
        : [directions.FRONT, directions.SIDES, directions.BACK];
    const distances = safetyDistanceUtils.getSafetyDistances(targetPlanKey, ixusAreas);

    const selectedIndexRef = useRef(selectedIndex);
    const armedDirectionRef = useRef(armedDirection);
    const targetPlanKeyRef = useRef(targetPlanKey);

    useEffect(() => {
        selectedIndexRef.current = selectedIndex;
    }, [selectedIndex]);

    useEffect(() => {
        armedDirectionRef.current = armedDirection;
    }, [armedDirection]);

    useEffect(() => {
        targetPlanKeyRef.current = targetPlanKey;
    }, [targetPlanKey]);

    useEffect(() => {
        setArmedDirection(undefined);
        setLevelPlanKey(levels.length > 0 ? levels[0].planKey : undefined);
    }, [selectedIndex]);

    const getLevelArea = () => ixusAreas.find(ixusArea => ixusArea.planKey === targetPlanKey);

    useEffect(() => {
        setNote(getLevelArea()?.distanceNote ?? null);
    }, [targetPlanKey]);

    const onChangeNote = value => {
        setNote(value);
        const levelArea = getLevelArea();
        if (levelArea) {
            levelArea.distanceNote = value;
            entryContext.setEntry(entry);
        }
    };

    // The group may have been moved since it was drawn, so the endpoints are read out via the
    // transform matrix instead of line.x1..y2 (service-desk#1668).
    const extractMeasurementCoords = group => {
        const [line] = group.getObjects();
        const scale = plot.refScale;
        return plot.getLineEndpoints(line).map(point => ({ x: point.x * scale, y: point.y * scale }));
    };

    const saveMeasurements = () => {
        const layer = plot.getLayer(measureLayerName);
        const measurements = [];
        layer.getObjects().forEach(obj => {
            if (obj.feature === "rect-feature") {
                measurements.push({
                    points: extractMeasurementCoords(obj),
                    direction: obj.direction ?? null,
                    levelPlanKey: obj.levelPlanKey ?? null,
                });
            }
        });

        const ixusArea = ixusAreas[selectedIndexRef.current];
        if (ixusArea?.calculation?.ixusResult) {
            ixusArea.calculation.ixusResult.measurements = measurements;
            entryContext.setEntry(entry);
        }
    };

    useEffect(() => {
        plot.addLayer(viewLayerName, 10);

        const mLayer = plot.addLayer(measureLayerName, 130);
        const feature = plot.applyLineFeature(mLayer, "green", false);
        feature.onSetText(dist => dist.toFixed(2) + "m");
        feature.onSelect(line => {
            setSelectedMeasurement(line);
            if (line) {
                setTimeout(() => saveMeasurements(), 0);
            }
        });
        feature.onModify(() => saveMeasurements());
        feature.onPlace(group => {
            const direction = armedDirectionRef.current;
            if (direction) {
                group.set({ direction, levelPlanKey: targetPlanKeyRef.current });
                setArmedDirection(undefined);
            }
            setTimeout(() => saveMeasurements(), 0);
        });
    }, []);

    const drawMeasurements = plan => {
        const layer = plot.getLayer(measureLayerName);
        ixusUtils.drawMeasurments(plan, plot, layer, ixusAreas);
    };

    useEffect(() => {
        const layer = plot.getLayer(measureLayerName);
        if (show && selectedPlan) {
            layer.activate();
            drawMeasurements(selectedPlan);
        } else {
            layer.removeObjects();
            setArmedDirection(undefined);
        }
    }, [show]);

    const onDelete = () => {
        const mLayer = plot.getLayer(measureLayerName);
        mLayer.removeObject(selectedMeasurement);
        setSelectedMeasurement(undefined);
        setTimeout(() => saveMeasurements(), 0);
    };


    useEffect(() => {
        const handleKeyDown = e => {
            if (!selectedMeasurement) {
                return;
            }
            if (e.key === "Delete" || e.key === "Backspace") {
                onDelete();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [selectedMeasurement]);

    return (
        <LayerStyle $show={show}>
            <Grid columns="1">
                <Button
                    svgIcon={mdiTrashCanOutline}
                    disabled={!selectedMeasurement}
                    onClick={() => onDelete()}
                    value={intl.get("common.delete")} />
            </Grid>
            {selectedIxusArea !== undefined &&
                <>
                    <Spacer variant="horizontal" />
                    <Grid columns="1">
                        {selectedIxusArea.sideView &&
                            <SelectSingle
                                name="ixus-measurement-level"
                                value={levelPlanKey ?? null}
                                title={intl.get("entry.ixus.measurement.level")}
                                onChange={value => setLevelPlanKey(value)}
                                toLabel={value => levels.find(level => level.planKey === value)?.label}>
                                {levels.map(level =>
                                    <option key={level.planKey} value={level.planKey}>{level.label}</option>
                                )}
                            </SelectSingle>
                        }
                        {armedDirection
                            ? <>
                                <div>{intl.get("entry.ixus.measurement.placeHint")}</div>
                                <Button
                                    onClick={() => setArmedDirection(undefined)}
                                    value={intl.get("common.cancel")} />
                            </>
                            : <Grid template="1fr 70px">
                                {availableDirections.map(direction =>
                                    <React.Fragment key={direction}>
                                        <Button
                                            disabled={targetPlanKey === undefined}
                                            onClick={() => setArmedDirection(direction)}
                                            value={intl.get("entry.ixus.measurement.direction." + direction)} />
                                        <Output
                                            textAlign="right"
                                            value={safetyDistanceUtils.formatDistance(distances[direction])} />
                                    </React.Fragment>
                                )}
                            </Grid>
                        }
                        <InputText
                            name="ixus-measurement-note"
                            value={note}
                            maxLength="255"
                            disabled={targetPlanKey === undefined}
                            onChange={onChangeNote}
                            title={intl.get("entry.ixus.measurement.note")} />
                    </Grid>
                </>
            }
        </LayerStyle>
    );
};
